import { useState } from "react";
import { toast } from "sonner";
import {
 Container, Section, PageHero, MapEmbed, CheckCircle2, ShieldCheck, Clock, Phone, MapPin,
 PHONE, PHONE_RAW,
} from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useServices, submitAppointment } from "../lib/api";
import { useSeo, breadcrumbLd } from "../lib/seo";

const EMPTY = { name: "", phone: "", email: "", vehicle: "", service: "", preferredDate: "", message: "" };

export default function Schedule() {
 const { data: services = [] } = useServices();
 const [form, setForm] = useState(EMPTY);
 const [sending, setSending] = useState(false);
 const [done, setDone] = useState(false);

 useSeo({
 title: "Schedule an Appointment | RPM Auto Repair Riverside",
 description: "Book your auto repair appointment online with RPM Auto Repair in Riverside, CA. Fast, honest, ASE-certified service. Or call (951) 653-4777.",
 path: "/schedule-appointment",
 jsonLd: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Schedule Appointment", path: "/schedule-appointment" }])],
 });

 const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

 const onSubmit = async (e) => {
 e.preventDefault();
 if (!form.name || !form.phone || !form.email) { toast.error("Please fill in your name, phone and email."); return; }
 setSending(true);
 try {
 await submitAppointment(form);
 setDone(true);
 toast.success("Request sent! We'll confirm your appointment shortly.");
 setForm(EMPTY);
 } catch {
 toast.error("Something went wrong. Please call us at " + PHONE);
 } finally { setSending(false); }
 };

 return (
 <>
 <PageHero eyebrow="Book Your Visit" title="Schedule an Appointment"
 subtitle="Tell us about your vehicle and we'll get you scheduled fast. Prefer to talk? Call us anytime at (951) 653-4777."
 image="https://images.pexels.com/photos/6870328/pexels-photo-6870328.jpeg?auto=compress&cs=tinysrgb&w=1400"
 crumbs={[{ name: "Home", path: "/" }, { name: "Schedule Appointment", path: "/schedule-appointment" }]} />
 <Section>
 <Container className="grid gap-12 lg:grid-cols-[1fr_380px]">
 <div>
 {done ? (
 <div className="rounded-2xl border border-primary/40 bg-primary/10 p-10 text-center" data-testid="appointment-success">
 <CheckCircle2 className="mx-auto text-primary" size={56} />
 <h2 className="mt-5 font-display text-3xl text-white">Request Received!</h2>
 <p className="mx-auto mt-3 max-w-md text-muted-foreground">Thanks for choosing RPM Auto Repair. Our team will reach out shortly to confirm your appointment. For anything urgent, call {PHONE}.</p>
 <button onClick={() => setDone(false)} className="mt-6 rounded-md border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:border-primary">Book Another</button>
 </div>
 ) : (
 <form onSubmit={onSubmit} className="space-y-5 rounded-2xl border border-white/10 bg-steel p-7" data-testid="appointment-form">
 <div className="grid gap-5 sm:grid-cols-2">
 <Field label="Full Name *"><Input required value={form.name} onChange={set("name")} data-testid="input-name" placeholder="Jane Doe" className="border-white/15 bg-ink text-white" /></Field>
 <Field label="Phone *"><Input required value={form.phone} onChange={set("phone")} data-testid="input-phone" placeholder="(951) 555-0100" className="border-white/15 bg-ink text-white" /></Field>
 </div>
 <Field label="Email *"><Input required type="email" value={form.email} onChange={set("email")} data-testid="input-email" placeholder="you@email.com" className="border-white/15 bg-ink text-white" /></Field>
 <div className="grid gap-5 sm:grid-cols-2">
 <Field label="Vehicle (Year / Make / Model)"><Input value={form.vehicle} onChange={set("vehicle")} data-testid="input-vehicle" placeholder="2018 Toyota Camry" className="border-white/15 bg-ink text-white" /></Field>
 <Field label="Preferred Date"><Input type="date" value={form.preferredDate} onChange={set("preferredDate")} data-testid="input-date" className="border-white/15 bg-ink text-white [color-scheme:dark]" /></Field>
 </div>
 <Field label="Service Needed">
 <select value={form.service} onChange={set("service")} data-testid="input-service"
 className="h-10 w-full rounded-md border border-white/15 bg-ink px-3 text-sm text-white">
 <option value="">Select a service…</option>
 {services.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
 <option value="Not sure / Other">Not sure / Other</option>
 </select>
 </Field>
 <Field label="Tell us more (symptoms, questions)"><Textarea rows={4} value={form.message} onChange={set("message")} data-testid="input-message" placeholder="Describe what's going on with your vehicle…" className="border-white/15 bg-ink text-white" /></Field>
 <button type="submit" disabled={sending} data-testid="submit-appointment"
 className="w-full rounded-md bg-primary py-4 font-semibold tracking-wide text-white transition-all hover:bg-racing-dark disabled:opacity-60">
 {sending ? "Sending…" : "Request Appointment"}
 </button>
 <p className="text-center text-xs text-muted-foreground">By submitting, you agree to be contacted about your appointment. We never share your information.</p>
 </form>
 )}
 </div>

 <aside className="space-y-6">
 <div className="rounded-xl border border-white/10 bg-steel p-6">
 <h3 className="font-display text-xl text-white">Contact Info</h3>
 <ul className="mt-4 space-y-3 text-sm text-white">
 <li><a href={`tel:${PHONE_RAW}`} className="flex items-center gap-3 hover:text-primary"><Phone size={17} className="text-primary" /> {PHONE}</a></li>
 <li className="flex items-center gap-3"><MapPin size={17} className="text-primary" /> 2650 E Alessandro Blvd, Riverside, CA 92508</li>
 <li className="flex items-center gap-3"><Clock size={17} className="text-primary" /> Mon-Fri 8-6 · Sat 8-2</li>
 </ul>
 </div>
 <MapEmbed />
 <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
 <ShieldCheck className="text-primary" size={26} />
 <p className="mt-3 text-sm text-muted-foreground">Every repair backed by a nationwide 24-month / 24,000-mile warranty. ASE Master Certified · AAA Approved · Financing available.</p>
 </div>
 </aside>
 </Container>
 </Section>
 </>
 );
}

const Field = ({ label, children }) => (
 <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">{label}</Label>{children}</div>
);
